// ¿El operador que mira el presupuesto es Viewer? (xcons/xcons-docs#488, #544)
//
// Un Viewer ve los inputs de la tabla de productos deshabilitados, pero SÍ puede
// confirmar el pedido. El nivel sale de dos lugares, y manda el MÁS ALTO, igual que
// `whichIsHigher` en la API (#544):
//
//   - el permiso del módulo Presupuestos en el Store, que viaja en el perfil
//     (`identity.permissions.store.quote[<storeCode>]`, de `GET /auth/profile`);
//   - el rol del operador como participante de ESTE presupuesto
//     (`quote.participations`, filas de operador: participationType store/seller).
//
// Un operador del Store que además es operador de un seller vinculado queda con dos
// filas —Admin en Store y la del par store-seller, que puede ser Lector— y la Lector
// no le baja el acceso. Es Viewer sólo si lo más alto que tiene es `viewer`.
// `by_relation` y `no_access` no son un nivel sobre ESTE presupuesto: no cuentan.
//
// Esto sólo decide qué se muestra. El gate de verdad es el backend.
import type { QuoteParticipation } from "./resolveOwner";

const isOperatorParticipation = (p: QuoteParticipation): boolean => {
  const type = String(p?.participationType ?? "").trim().toLowerCase();
  return type === "store" || type === "seller";
};

const participationUserId = (p: QuoteParticipation): number | null => {
  const raw = p?.contact?.user?.userId ?? p?.userId;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export interface QuoteViewerInput {
  /** Nivel del módulo Presupuestos en el Store del presupuesto. */
  storeQuoteLevel?: string | null;
  participations?: QuoteParticipation[] | null;
  /** `identity.id` del operador logueado. */
  userId?: number | null;
}

const LEVEL_RANK: Record<string, number> = { viewer: 1, editor: 2, admin: 3 };

const levelRank = (value: unknown): number =>
  LEVEL_RANK[String(value ?? "").trim().toLowerCase()] ?? 0;

export const isQuoteViewer = ({
  storeQuoteLevel,
  participations,
  userId,
}: QuoteViewerInput): boolean => {
  const ranks = [levelRank(storeQuoteLevel)];
  if (userId != null) {
    for (const p of participations ?? []) {
      if (
        isOperatorParticipation(p) &&
        (p.permissionCode == null || p.permissionCode === "quote") &&
        participationUserId(p) === Number(userId)
      ) {
        ranks.push(levelRank(p.value));
      }
    }
  }
  return Math.max(...ranks) === LEVEL_RANK.viewer;
};
